import { type JSX, splitProps } from 'solid-js';

// The button primitive (plan doc 17 step 1). Its job is to be the ONLY place a
// `btn--*` class string is written, because a hand-typed one has no reader: the
// browser drops a modifier that does not exist and paints the base rule, so the
// mistake is silent everywhere except on screen.
//
// That is not hypothetical. Four call sites shipped modifiers `ui/_button.scss`
// never declared — `btn--danger` on the three Stop controls and `btn--primary`
// on the service-worker Reload — and each rendered as whatever the modifier
// BESIDE it said. `btn btn--outline btn--danger` painted a neutral outline; on
// the queue board `btn btn--ghost btn--sm btn--danger` computed byte-for-byte
// identically to the "Details" ghost button sitting next to it, so the only
// control that ends a run looked exactly like the one that opens a drawer.
//
// `variant` and `size` are closed unions, so `variant="danger"` is a compile
// error rather than a dead class. The union is joined against the declared
// modifiers by `styles/button-modifiers.test.ts`, which also sweeps every other
// `btn--*` spelling in source (the raw-string migration reached zero; what is
// left is this table and the tests that assert it).

/**
 * `primary` deliberately maps to NO modifier: `.btn`'s own rule is the primary
 * fill (`background: var(--primary)`), so there is nothing for a `btn--primary`
 * to add. That is exactly why the one written in `UpdatePrompt` was inert and
 * looked correct — it asked for the default and got it. Naming the default here
 * means a caller can still SAY "primary" without inventing a class for it.
 *
 * `brand` is the ONE accent-filled call to action a view may carry (owner ruling,
 * 2026-09-16): onboarding's advance action is its user. It is a `BRAND_CLASSES`
 * row in `scripts/accent-discipline.test.ts` and a row in design-system.md
 * § Accent. Per-row actions (APPROVE / SKIP) stay `outline`; `primary` stays the
 * ink fill. Nothing types "one per view" — review owns it.
 */
export const BUTTON_VARIANTS = [
  'primary',
  'brand',
  'outline',
  'secondary',
  'ghost',
  'destructive',
  'link',
] as const;
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

export const BUTTON_SIZES = ['md', 'sm', 'icon', 'icon-sm'] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: '',
  brand: 'btn--brand',
  outline: 'btn--outline',
  secondary: 'btn--secondary',
  ghost: 'btn--ghost',
  destructive: 'btn--destructive',
  link: 'btn--link',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  md: '',
  sm: 'btn--sm',
  icon: 'btn--icon',
  'icon-sm': 'btn--icon-sm',
};

export type ButtonLook = {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
};

/**
 * The class join itself, for the three shapes `<Button/>` is the wrong element
 * for. The first two were measured across `components/` before this existed —
 * 260 raw sites, 224 of them a `<button>`:
 *
 *  1. An ANCHOR — 36 sites wearing `class="btn …"` on a link. `<Button/>` emits
 *     `<button>`, and swapping the element would break navigation and
 *     middle-click. The two link flavours are NOT interchangeable with each
 *     other either: 14 use the router's `<A>` (client-side nav) and 22 a plain
 *     `<a>` (full load, `target="_blank"`, external), so a single `ButtonLink`
 *     element wrapper would have to pick one and silently change the other's
 *     behaviour. A polymorphic `as=` prop is the same hole `class` is refused
 *     for, one level up. What has to be single-sourced is the MODIFIER STRING,
 *     not the tag — so this returns the string and the call site keeps its
 *     element. (The prior pass's note called this a missing `ButtonLink`; the
 *     measurement is why it is a function instead.)
 *  2. An EXTRA class beside the modifiers — 24 sites carrying a BEM element class
 *     the parent block owns (`dialog__close`, `pager__step`, `app-topbar__menu`);
 *     3 of those are also anchors. `<Button/>` deliberately does not forward
 *     `class` and that stands; moving those rules onto a wrapper is a DOM change
 *     on a visual surface. Composing `` `${buttonClass({ … })} dialog__close` ``
 *     leaves exactly the page class hand-written and takes the `btn--*` half off
 *     the call site, which is the half that had no reader.
 *  3. A `type="submit"` button — the primary action of a real form. `<Button/>`
 *     always emits `type="button"`, so it cannot BE one; the reasoning for
 *     refusing a `type` prop rather than adding one is on `Button` below.
 *
 * This is a string builder, not an escape hatch for `<Button/>`: it cannot widen
 * the union, so it emits nothing `styles/button-modifiers.test.ts` does not
 * already join against the stylesheet.
 */
export function buttonClass(look?: ButtonLook): string {
  return ['btn', VARIANT_CLASS[look?.variant ?? 'primary'], SIZE_CLASS[look?.size ?? 'md']]
    .filter((part) => part !== '')
    .join(' ');
}

export type ButtonProps = ButtonLook &
  Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'class' | 'classList' | 'type'>;

/**
 * `class` is deliberately NOT forwarded. An escape hatch that accepts arbitrary
 * classes is how the fifth undefined modifier gets typed anyway, one prop away
 * from the union that exists to refuse it. A caller needing a class the design
 * system does not have needs a variant, not a hole.
 */
export function Button(props: ButtonProps): JSX.Element {
  const [local, rest] = splitProps(props, ['variant', 'size']);
  // Always `type="button"`. A bare <button> defaults to `submit`, so one placed
  // inside a form posts it — the reason `type` is stripped from the prop type
  // rather than defaulted: a submit button is a different component's job.
  //
  // A submit button is therefore a THIRD shape this component is wrong for, and
  // slice 17 settled it the same way as the other two: EVERY `type="submit"`
  // button under `components/` keeps its element and its `type` and takes its
  // class from `buttonClass()`. Giving this component a `type` prop was the
  // alternative and was refused — it makes the accident of a form-posting
  // `<Button/>` representable again, one prop away from the union that exists
  // to refuse exactly that, and a `SubmitButton` wrapper is the same hole with
  // a nicer name until something needs more than the class string.
  return <button {...rest} type="button" class={buttonClass(local)} />;
}

/**
 * Every class this component can emit. Exported for `styles/button-modifiers.test.ts`
 * to join against the declared modifiers — a variant table is only as good as its
 * agreement with the stylesheet, and that agreement is not something TypeScript
 * can check.
 */
export const BUTTON_CLASSES: readonly string[] = [
  ...Object.values(VARIANT_CLASS),
  ...Object.values(SIZE_CLASS),
].filter((cls) => cls !== '');
